import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Pagination,
  Stack,
  Chip,
  Alert,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Zoom,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Snackbar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import RefreshIcon from '@mui/icons-material/Refresh';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { RsocNavHeader, PageHeader } from './';
import Api from '../../Services/Api';

// Special ad categories for Facebook campaigns
const specialAdCategories = [
  { value: 'NONE', label: 'None', description: 'No special category' },
  { value: 'FINANCIAL_PRODUCTS_SERVICES', label: 'Financial Products & Services', description: 'Ads related to credit cards, loans, insurance, investment advice, or other financial services' },
  { value: 'EMPLOYMENT', label: 'Employment', description: 'Ads related to job opportunities or employment' },
  { value: 'HOUSING', label: 'Housing', description: 'Ads related to housing, real estate, or mortgage' },
  { value: 'ISSUES_ELECTIONS_POLITICS', label: 'Social issues, elections or politics', description: 'Ads about social issues, elections, or politics' },
];

/**
 * RSOC Inspiration component
 * Shows inspiration data from rsoc_spy table with filtering and search
 */
const RsocInspiration = () => {
  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  
  // Filter state
  const [filterStatus, setFilterStatus] = useState('pending'); // 'all', 'created', 'pending'
  const [hidingItemId, setHidingItemId] = useState(null); // For animation when hiding
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(20);



  // Inline title editing state
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  // Inline keywords editing state
  const [editingKeywordsId, setEditingKeywordsId] = useState(null);
  const [editedKeywords, setEditedKeywords] = useState('');
  const [savingKeywords, setSavingKeywords] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Generate content state
  const [generatingContent, setGeneratingContent] = useState({});
  const [generationLogs, setGenerationLogs] = useState({});

  // Category selection dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedItemForGeneration, setSelectedItemForGeneration] = useState(null);
  const [allCategories, setAllCategories] = useState([]); // Store all categories for autocomplete
  const [dialogSelectedCategory, setDialogSelectedCategory] = useState(null);
  const [dialogCategoryOptions, setDialogCategoryOptions] = useState([]);
  const [dialogLoadingCategories, setDialogLoadingCategories] = useState(false);

  // Special ad categories selector state
  const [dialogSelectedSpecialAdCategories, setDialogSelectedSpecialAdCategories] = useState(['NONE']);

  // CSV upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [blacklistWords, setBlacklistWords] = useState('');

  // Manual entry state
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualTitle, setManualTitle] = useState('');
  const [manualKeywords, setManualKeywords] = useState('');
  const [manualSpecialCategories, setManualSpecialCategories] = useState([]);
  const [savingManual, setSavingManual] = useState(false);
  const fileInputRef = useRef(null);

  // Selection state for bulk operations
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkExecuting, setBulkExecuting] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Async CSV processing state
  const [csvJobId, setCsvJobId] = useState(null);
  const [csvJobStatus, setCsvJobStatus] = useState(null);
  const [csvProgress, setCsvProgress] = useState(0);
  const [csvProcessingMessage, setCsvProcessingMessage] = useState('');

  // CSV confirmation dialog state
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [selectedPendingItems, setSelectedPendingItems] = useState([]);
  const [confirmingItems, setConfirmingItems] = useState(false);

  // Fetch inspiration data
  const fetchInspirationData = async (page = 1) => {
    setLoading(true);
    setError('');

    try {
      console.log('Fetching inspiration data...', { page, per_page: itemsPerPage, status: filterStatus });

      const response = await Api.getRsocInspiration({
        page,
        per_page: itemsPerPage,
        status: filterStatus
      });

      console.log('API Response:', response);

      if (response?.success) {
        setData(response.data || []);
        setCategories(response.categories || []);

        // Update pagination info
        const pagination = response.pagination;
        setCurrentPage(pagination.current_page);
        setTotalPages(pagination.last_page);
        setTotalItems(pagination.total);
      } else {
        throw new Error('Failed to fetch inspiration data');
      }
    } catch (err) {
      console.error('Error fetching inspiration data:', err);
      console.error('Error details:', err.response);
      setError('Failed to load inspiration data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  // Load all categories once for autocomplete
  const loadAllCategories = async () => {
    try {
      console.log('Loading all categories for autocomplete...');
      const response = await Api.getLocalCategories({ all: true });

      if (response && response.data) {
        const mappedCategories = response.data.map(category => ({
          id: category.id,
          name: category.name,
          parent_id: category.parent_id || null
        }));

        setAllCategories(mappedCategories);
        console.log(`Loaded ${mappedCategories.length} categories for autocomplete`);
      }
    } catch (error) {
      console.error('Error loading all categories:', error);
    }
  };

  useEffect(() => {
    fetchInspirationData();
    loadAllCategories(); // Load all categories for autocomplete
  }, []);

  // Re-fetch data when filter status changes
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when changing filters
    setSelectedItems([]); // Clear selection when filter changes
    fetchInspirationData(1);
  }, [filterStatus]);

  // When categories are loaded, trigger initial search if dialog is open
  useEffect(() => {
    if (allCategories.length > 0 && categoryDialogOpen && dialogCategoryOptions.length === 0) {
      searchDialogCategories(); // Populate initial categories
    }
  }, [allCategories]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (window.categorySearchTimeout) {
        clearTimeout(window.categorySearchTimeout);
      }
    };
  }, []);

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    fetchInspirationData(page);
  };

  // Search categories locally (no API calls needed)
  const searchDialogCategories = async (query = '') => {
    setDialogLoadingCategories(true);

    try {
      // If allCategories is not loaded yet, load them first
      if (allCategories.length === 0) {
        await loadAllCategories();
        return; // This will trigger another search once categories are loaded
      }

      // Filter categories locally for instant results
      let filteredCategories = allCategories;

      if (query && query.trim() !== '') {
        const searchLower = query.toLowerCase();
        filteredCategories = allCategories.filter(category =>
          category.name.toLowerCase().includes(searchLower)
        );
      }

      // Limit results for performance (first 100 matches)
      const limitedCategories = filteredCategories.slice(0, 100);

      setDialogCategoryOptions(limitedCategories);
      console.log(`Filtered to ${limitedCategories.length} categories matching "${query}"`);

    } catch (error) {
      console.error('Error filtering categories:', error);
    } finally {
      setDialogLoadingCategories(false);
    }
  };


  // Handle start title editing
  const handleStartTitleEditing = (item) => {
    setEditingTitleId(item.id);
    setEditedTitle(item.title);
  };

  // Handle cancel title editing
  const handleCancelTitleEditing = () => {
    setEditingTitleId(null);
    setEditedTitle('');
  };

  // Handle save title
  const handleSaveTitle = async () => {
    if (!editingTitleId || !editedTitle.trim()) return;
    
    setSavingTitle(true);
    try {
      const response = await Api.updateRsocInspirationTitle(editingTitleId, editedTitle.trim());
      
      if (response?.success) {
        // Update the local data
        setData(prevData => 
          prevData.map(item => 
            item.id === editingTitleId 
              ? { ...item, title: editedTitle.trim() }
              : item
          )
        );
        
        setEditingTitleId(null);
        setEditedTitle('');
        
        // Refresh the page data to ensure consistency
        fetchInspirationData(currentPage);
      } else {
        setError('Failed to update title');
      }
    } catch (err) {
      console.error('Error updating title:', err);
      setError('Failed to update title');
    } finally {
      setSavingTitle(false);
    }
  };

  // Handle start keywords editing
  const handleStartKeywordsEditing = (item) => {
    setEditingKeywordsId(item.id);
    setEditedKeywords(item.keywords);
  };

  // Handle cancel keywords editing
  const handleCancelKeywordsEditing = () => {
    setEditingKeywordsId(null);
    setEditedKeywords('');
  };

  // Handle save keywords
  const handleSaveKeywords = async () => {
    if (!editingKeywordsId || !editedKeywords.trim()) return;
    
    setSavingKeywords(true);
    try {
      const response = await Api.updateRsocInspirationKeywords(editingKeywordsId, editedKeywords.trim());
      
      if (response?.success) {
        // Update the local data
        setData(prevData => 
          prevData.map(item => 
            item.id === editingKeywordsId 
              ? { ...item, keywords: editedKeywords.trim() }
              : item
          )
        );
        
        setEditingKeywordsId(null);
        setEditedKeywords('');
        
        // Refresh the page data to ensure consistency
        fetchInspirationData(currentPage);
      } else {
        setError('Failed to update keywords');
      }
    } catch (err) {
      console.error('Error updating keywords:', err);
      setError('Failed to update keywords');
    } finally {
      setSavingKeywords(false);
    }
  };



  // Handle delete confirmation
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  // Handle delete item
  const handleDeleteItem = async () => {
    if (!itemToDelete) return;

    setDeleting(true);
    try {
      console.log('Deleting item:', itemToDelete);
      
      const response = await Api.deleteRsocInspiration(itemToDelete.id);
      
      console.log('Delete response:', response);
      
      if (response?.success) {
        // Remove item from local data
        setData(prevData => prevData.filter(item => item.id !== itemToDelete.id));
        
        // Update total count
        setTotalItems(prev => prev - 1);
        
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        
        // If current page becomes empty and it's not the first page, go to previous page
        if (data.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
          fetchInspirationData(currentPage - 1);
        } else {
          // Refresh current page to ensure consistency
          fetchInspirationData(currentPage);
        }
      } else {
        setError('Failed to delete inspiration item');
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Failed to delete inspiration item');
    } finally {
      setDeleting(false);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };


  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      'travel': 'info',
      'health': 'success',
      'home improvement': 'warning',
      'employment': 'secondary',
      'senior care': 'primary',
      'fashion': 'error',
      'business': 'default',
      'fitness': 'success',
      'beauty': 'error',
      'automotive': 'info',
      'finance': 'warning'
    };
    return colors[category] || 'default';
  };

  // Truncate text
  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Parse keywords and get first 5
  const parseKeywords = (keywordsString) => {
    if (!keywordsString) return [];
    return keywordsString.split(',').map(k => k.trim()).filter(k => k).slice(0, 5);
  };

  // Get display data (now only excludes items being hidden with animation)
  const getDisplayData = () => {
    if (!data) return [];

    // Exclude items that are being hidden with animation
    return data.filter(item => item.id !== hidingItemId);
  };

  // Handle generate content page button click
  const handleGenerateContentClick = (item, forceReExecute = false) => {
    // If content exists and not forcing re-execution, show confirmation
    if (item.page_id && !forceReExecute) {
      const confirmed = window.confirm(
        `Content already exists for "${item.title}". Do you want to re-generate it?\n\nThis will create a new version and may replace the existing content.`
      );
      if (!confirmed) {
        return;
      }
      // Proceed with re-execution
      console.log('Re-executing content generation for item:', item.title);
    }

    // Prepare category for generation (use existing if available, otherwise null)
    const categoryForGeneration = (item.category_id && item.category) ? {
      id: item.category_id,
      name: item.category,
      parent_id: null
    } : null;

    // Prepare special ad categories (use existing if available, otherwise default to NONE)
    const specialAdCategoriesForGeneration = Array.isArray(item.special_ad_categories) && item.special_ad_categories.length > 0
      ? item.special_ad_categories
      : ['NONE'];

    // Execute directly without category dialog
    console.log('Executing content generation for item:', item.title);
    handleGenerateContentPage(item, categoryForGeneration, specialAdCategoriesForGeneration);
  };

  // Handle category dialog confirmation
  const handleCategoryDialogConfirm = () => {
    if (!dialogSelectedCategory || !selectedItemForGeneration) return;

    // Close dialog
    setCategoryDialogOpen(false);

    // Start content generation with selected category and special ad categories
    handleGenerateContentPage(selectedItemForGeneration, dialogSelectedCategory, dialogSelectedSpecialAdCategories);
  };

  // Handle category dialog cancel
  const handleCategoryDialogCancel = () => {
    setCategoryDialogOpen(false);
    setSelectedItemForGeneration(null);
    setDialogSelectedCategory(null);
    setDialogSelectedSpecialAdCategories(['NONE']);
  };

  // CSV Upload handlers
  const handleOpenUploadDialog = () => {
    setUploadDialogOpen(true);
    setUploadFile(null);
    setUploadResults(null);
    setUploadProgress(0);
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setUploadFile(null);
    setUploadResults(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setUploadFile(file);
      setUploadResults(null);
    } else {
      setError('Please select a valid CSV file');
    }
  };

  // Manual entry handlers
  const handleOpenManualDialog = () => {
    setManualDialogOpen(true);
    setManualTitle('');
    setManualKeywords('');
    setManualSpecialCategories([]);
  };

  const handleCloseManualDialog = () => {
    setManualDialogOpen(false);
    setManualTitle('');
    setManualKeywords('');
    setManualSpecialCategories([]);
  };

  const handleSaveManualEntry = async () => {
    if (!manualTitle.trim()) {
      setError('Title is required');
      return;
    }
    if (!manualKeywords.trim()) {
      setError('Keywords are required');
      return;
    }

    setSavingManual(true);
    setError('');

    try {
      const data = {
        title: manualTitle.trim(),
        keywords: manualKeywords.trim(),
        special_ad_categories: manualSpecialCategories.length > 0 ? manualSpecialCategories : null
      };

      const response = await Api.createManualRsocInspiration(data);

      if (response.success && response.data) {
        setSnackbarMessage('Manual entry created! Starting content generation...');
        setSnackbarOpen(true);
        handleCloseManualDialog();

        // Automatically execute content generation for the newly created entry
        const newItem = response.data;

        // Prepare category (null if not available)
        const categoryForGeneration = null;

        // Prepare special ad categories from the manual entry
        const specialAdCategoriesForGeneration = manualSpecialCategories.length > 0
          ? manualSpecialCategories
          : ['NONE'];

        // Execute content generation immediately
        console.log('Auto-executing content generation for manual entry:', newItem.title);
        await handleGenerateContentPage(newItem, categoryForGeneration, specialAdCategoriesForGeneration);

        // Reload data to show the executed item
        fetchInspirationData();
      } else {
        setError(response.error || 'Failed to create manual entry');
      }
    } catch (error) {
      console.error('Error creating manual entry:', error);
      setError('Failed to create manual entry');
    } finally {
      setSavingManual(false);
    }
  };

  const handleUploadCSV = async () => {
    if (!uploadFile) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setUploadProgress(10);
    setUploadResults(null);
    setCsvJobId(null);
    setCsvJobStatus('uploading');
    setCsvProgress(0);
    setCsvProcessingMessage('Uploading CSV file...');

    try {
      // Send to backend for async processing
      const formData = new FormData();
      formData.append('csv_file', uploadFile);
      if (blacklistWords.trim()) {
        formData.append('blacklist_words', blacklistWords.trim());
      }

      const response = await Api.uploadRsocInspirationCSV(formData);

      setUploadProgress(100);

      if (response?.success && response.jobId) {
        // File uploaded successfully, start monitoring job
        setCsvJobId(response.jobId);
        setCsvJobStatus('processing');
        setCsvProcessingMessage('File uploaded. Processing in background...');

        // Start polling for job status
        startJobStatusPolling(response.jobId);

        // Show immediate feedback
        setSnackbarMessage('CSV uploaded successfully! Processing in background...');
        setSnackbarOpen(true);

      } else {
        setUploadResults({
          success: false,
          error: response?.message || 'Failed to upload CSV file'
        });
        setCsvJobStatus('failed');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setUploadResults({
        success: false,
        error: error?.message || 'An error occurred while uploading the CSV file'
      });
      setCsvJobStatus('failed');
    } finally {
      setUploading(false);
    }
  };

  // Poll for job status
  const startJobStatusPolling = (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await Api.checkCSVJobStatus(jobId);

        if (statusResponse.success) {
          setCsvJobStatus(statusResponse.status);
          setCsvProgress(statusResponse.progress || 0);
          setCsvProcessingMessage(statusResponse.message || 'Processing...');

          if (statusResponse.status === 'pending_confirmation') {
            clearInterval(pollInterval);

            // Validation complete - show confirmation dialog
            const results = statusResponse.results;
            const pendingItemsList = results?.pending_items || [];

            setCsvJobStatus('pending_confirmation');
            setCsvProcessingMessage(`Validation complete! ${pendingItemsList.length} items ready for review.`);

            // Set pending items for confirmation dialog
            setPendingItems(pendingItemsList);
            // Select all by default
            setSelectedPendingItems(pendingItemsList.map(item => item.temp_id));

            // Show skipped info in upload results
            const summary = results?.summary || {};
            const allSkipped = [
              ...(results?.skipped?.pre_filter || []),
              ...(results?.skipped?.gpt_rejected || []),
              ...(results?.skipped?.post_filter || [])
            ];

            setUploadResults({
              success: true,
              requiresConfirmation: true,
              summary: `${summary.original_rows || 0} loaded → ${summary.pending_confirmation || 0} ready for confirmation`,
              detailedSummary: summary,
              pendingItems: pendingItemsList,
              skipped: allSkipped,
              skippedByCategory: results?.skipped || {},
              errors: results?.errors || []
            });

            // Open confirmation dialog
            setConfirmationDialogOpen(true);

            // Show info message
            setSnackbarMessage(`${pendingItemsList.length} items ready for review. Please confirm which items to add.`);
            setSnackbarOpen(true);

          } else if (statusResponse.status === 'completed') {
            clearInterval(pollInterval);

            // Show final results with detailed breakdown
            const results = statusResponse.results;
            const summary = results?.summary || {};

            // Build detailed summary string
            const summaryParts = [
              `${summary.original_rows || 0} rows loaded`,
              `${summary.pre_filter_skipped || 0} pre-filtered`,
              `${summary.sent_to_gpt || 0} sent to GPT`,
              `${summary.gpt_rejected || 0} GPT rejected`,
              `${summary.gpt_accepted || 0} GPT accepted`,
              `${summary.post_filter_skipped || 0} post-filtered`,
              `${summary.added || 0} added`,
              `${summary.errors || 0} errors`
            ];

            // Flatten skipped arrays for display
            const allSkipped = [
              ...(results?.skipped?.pre_filter || []),
              ...(results?.skipped?.gpt_rejected || []),
              ...(results?.skipped?.post_filter || [])
            ];

            setUploadResults({
              success: true,
              summary: summaryParts.join(' → '),
              detailedSummary: summary,
              added: results?.added || [],
              skipped: allSkipped,
              skippedByCategory: results?.skipped || {},
              errors: results?.errors || []
            });

            setCsvJobStatus('completed');
            setCsvProcessingMessage('Processing completed!');

            // Refresh the data to show new items
            fetchInspirationData(currentPage);

            // Show success message
            setSnackbarMessage(`CSV processing completed! ${results?.summary?.added || 0} items added.`);
            setSnackbarOpen(true);

          } else if (statusResponse.status === 'failed') {
            clearInterval(pollInterval);
            setUploadResults({
              success: false,
              error: statusResponse.message || 'Processing failed'
            });
            setCsvJobStatus('failed');
            setCsvProcessingMessage('Processing failed');

            // Show error message
            setSnackbarMessage('CSV processing failed. Please try again.');
            setSnackbarOpen(true);
          }
        } else {
          // Job not found or other error
          if (statusResponse.status === 'not_found') {
            clearInterval(pollInterval);
            setCsvJobStatus('failed');
            setCsvProcessingMessage('Job not found');
          }
        }
      } catch (error) {
        console.error('Error checking job status:', error);
        // Continue polling, don't stop on temporary errors
      }
    }, 2000); // Poll every 2 seconds

    // Store interval ID for cleanup if needed
    setCsvJobId(prevId => {
      if (prevId) {
        // Clear any existing intervals
        clearInterval(window[`pollInterval_${prevId}`]);
      }
      window[`pollInterval_${jobId}`] = pollInterval;
      return jobId;
    });
  };

  // Download CSV template
  const handleDownloadTemplate = () => {
    const template = 'Title,Keywords\n"Example Title 1","keyword1,keyword2,keyword3,keyword4,keyword5"\n"Example Title 2","keyword1,keyword2,keyword3"';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rsoc_inspiration_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Handle pending item selection toggle
  const handleTogglePendingItem = (tempId) => {
    setSelectedPendingItems(prev => {
      if (prev.includes(tempId)) {
        return prev.filter(id => id !== tempId);
      } else {
        return [...prev, tempId];
      }
    });
  };

  // Handle select all pending items
  const handleSelectAllPending = () => {
    if (selectedPendingItems.length === pendingItems.length) {
      setSelectedPendingItems([]);
    } else {
      setSelectedPendingItems(pendingItems.map(item => item.temp_id));
    }
  };

  // Handle confirm selected items
  const handleConfirmItems = async () => {
    if (selectedPendingItems.length === 0) {
      setSnackbarMessage('Please select at least one item to add.');
      setSnackbarOpen(true);
      return;
    }

    setConfirmingItems(true);

    try {
      const response = await Api.confirmCSVItems(csvJobId, selectedPendingItems);

      if (response.success) {
        setSnackbarMessage(`Successfully added ${response.summary?.inserted || 0} items!`);
        setSnackbarOpen(true);

        // Update results to show what was added
        setUploadResults(prev => ({
          ...prev,
          requiresConfirmation: false,
          added: response.added || [],
          detailedSummary: {
            ...prev.detailedSummary,
            added: response.summary?.inserted || 0
          }
        }));

        // Close confirmation dialog
        setConfirmationDialogOpen(false);
        setPendingItems([]);
        setSelectedPendingItems([]);
        setCsvJobStatus('completed');

        // Refresh the data to show new items
        fetchInspirationData(currentPage);
      } else {
        setSnackbarMessage(response.message || 'Failed to add items');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error confirming items:', error);
      setSnackbarMessage('Failed to add items. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setConfirmingItems(false);
    }
  };

  // Handle cancel confirmation (close dialog without adding)
  const handleCancelConfirmation = () => {
    setConfirmationDialogOpen(false);
    setPendingItems([]);
    setSelectedPendingItems([]);
    setSnackbarMessage('CSV confirmation cancelled. No items were added.');
    setSnackbarOpen(true);
  };

  // Handle generate content page (with category and special ad categories provided)
  const handleGenerateContentPage = async (item, selectedCategory, specialAdCategories = ['NONE']) => {
    if (item.page_id) {
      console.log('Content already created for this item');
      return;
    }

    const itemId = item.id;
    setGeneratingContent(prev => ({ ...prev, [itemId]: true }));
    setGenerationLogs(prev => ({ ...prev, [itemId]: [] }));

    const addLog = (message) => {
      console.log(message);
      setGenerationLogs(prev => ({
        ...prev,
        [itemId]: [...(prev[itemId] || []), message]
      }));
    };

    try {
      addLog('Starting content generation job...');

      // Log category info (optional)
      if (selectedCategory?.id) {
        addLog(`✅ Using category: ${selectedCategory.name} (ID: ${selectedCategory.id})`);
      } else {
        addLog('ℹ️ No category specified - content will be generated without category');
      }

      // Get default domain (first available domain)
      addLog('🔍 Loading available domains...');
      const domainsResponse = await Api.searchDomains();
      if (!domainsResponse?.data || domainsResponse.data.length === 0) {
        addLog('❌ Error: No domains available.');
        setError('No domains available for content generation.');
        return;
      }

      const defaultDomain = domainsResponse.data[0]; // Use first domain as default
      addLog(`✅ Using domain: ${defaultDomain.domain_name}`);

      // Parse keywords
      const keywords = parseKeywords(item.keywords);
      if (keywords.length === 0) {
        addLog('❌ Error: At least 1 keyword is required.');
        setError('At least 1 keyword is required for content generation.');
        return;
      }
      addLog(`✅ Using keywords: ${keywords.join(', ')}`);

      // Start the background job
      addLog('🚀 Dispatching content generation job...');
      const jobResponse = await Api.generateRsocContentJob(
        item,
        selectedCategory,
        specialAdCategories,
        defaultDomain
      );

      if (!jobResponse?.success) {
        const errorMsg = jobResponse?.message || 'Failed to start content generation job';
        addLog(`❌ Job dispatch failed: ${errorMsg}`);
        setError(errorMsg);
        return;
      }

      addLog('✅ Content generation job started successfully!');
      addLog(`📋 Job ID: ${jobResponse.job_id || 'N/A'}`);
      addLog('⏳ Content generation is now running in the background...');
      addLog('🔄 Please refresh the page to see updates or check back in a few minutes.');

      // Update UI to show job is executed
      setData(prevData =>
        prevData.map(dataItem =>
          dataItem.id === itemId
            ? { ...dataItem, job_status: 'processing', executed: true }
            : dataItem
        )
      );

      // Show success message
      setTimeout(() => {
        addLog('✅ Job submitted successfully! The content will be generated in the background.');
        addLog('💡 You can continue working while the content is being created.');

        // If we're in pending filter mode and job is queued, show completion animation
        if (filterStatus === 'pending') {
          addLog('🎉 Job queued successfully!');
        }
      }, 1000);

    } catch (error) {
      console.error('Error dispatching content generation job:', error);
      const errorMsg = error?.response?.data?.message || error.message || 'An error occurred while starting content generation';
      addLog(`❌ Error: ${errorMsg}`);
      setError(errorMsg);
    } finally {
      // Don't set generating to false immediately - keep it true to show job is running
      // Only set to false after a delay to show the job was dispatched
      setTimeout(() => {
        setGeneratingContent(prev => ({ ...prev, [itemId]: false }));
      }, 2000);
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Select all items on current page that are not executed and don't have content
      const selectableItems = getDisplayData().filter(item =>
        !item.executed && !item.page_id
      ).map(item => item.id);
      setSelectedItems(selectableItems);
    } else {
      setSelectedItems([]);
    }
  };

  // Handle individual item selection
  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Check if all selectable items are selected
  const isAllSelected = () => {
    const selectableItems = getDisplayData().filter(item =>
      !item.executed && !item.page_id
    );
    return selectableItems.length > 0 &&
           selectableItems.every(item => selectedItems.includes(item.id));
  };

  // Check if some items are selected
  const isSomeSelected = () => {
    const selectableItems = getDisplayData().filter(item =>
      !item.executed && !item.page_id
    );
    return selectedItems.length > 0 &&
           selectedItems.length < selectableItems.length;
  };

  // Handle bulk execute
  const handleBulkExecute = async () => {
    if (selectedItems.length === 0) {
      setSnackbarMessage('No items selected');
      setSnackbarOpen(true);
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to execute ${selectedItems.length} items? This will generate content for all selected items.`
    );

    if (!confirmed) {
      return;
    }

    setBulkExecuting(true);
    setSnackbarMessage(`Executing ${selectedItems.length} items...`);
    setSnackbarOpen(true);

    let successCount = 0;
    let failCount = 0;

    // Process each selected item
    for (const itemId of selectedItems) {
      const item = data.find(d => d.id === itemId);
      if (!item) continue;

      try {
        // Prepare category for generation (use existing if available, otherwise null)
        const categoryForGeneration = (item.category_id && item.category) ? {
          id: item.category_id,
          name: item.category,
          parent_id: null
        } : null;

        // Prepare special ad categories (use existing if available, otherwise default to NONE)
        const specialAdCategoriesForGeneration = Array.isArray(item.special_ad_categories) && item.special_ad_categories.length > 0
          ? item.special_ad_categories
          : ['NONE'];

        // Execute content generation
        await handleGenerateContentPage(item, categoryForGeneration, specialAdCategoriesForGeneration);
        successCount++;
      } catch (error) {
        console.error(`Error executing item ${itemId}:`, error);
        failCount++;
      }
    }

    // Clear selection after bulk execute
    setSelectedItems([]);
    setBulkExecuting(false);

    // Show results
    setSnackbarMessage(`Bulk execution completed: ${successCount} succeeded, ${failCount} failed/skipped`);
    setSnackbarOpen(true);

    // Refresh data
    fetchInspirationData(currentPage);
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      if (csvJobId && window[`pollInterval_${csvJobId}`]) {
        clearInterval(window[`pollInterval_${csvJobId}`]);
        delete window[`pollInterval_${csvJobId}`];
      }
    };
  }, [csvJobId]);

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% {
              background-color: inherit;
            }
            50% {
              background-color: #c8e6c9;
            }
            100% {
              background-color: inherit;
            }
          }
        `}
      </style>
      <Container maxWidth="xl">
      <PageHeader 
        title="RSOC Inspiration"
        subtitle="Browse titles and keywords for content inspiration from our curated database"
      />
      
      <RsocNavHeader />

      {/* Summary Section & Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" color="text.secondary">
              Total: {totalItems} inspiration{totalItems !== 1 ? 's' : ''} {filterStatus !== 'all' ? `(${filterStatus})` : ''} available
            </Typography>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleOpenManualDialog}
              size="small"
              sx={{ ml: 2 }}
            >
              Add Manual Entry
            </Button>

            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={handleOpenUploadDialog}
              size="small"
              sx={{ ml: 1 }}
            >
              Upload CSV
            </Button>

            {selectedItems.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={bulkExecuting ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                onClick={handleBulkExecute}
                disabled={bulkExecuting}
                size="small"
                sx={{ ml: 1 }}
              >
                {bulkExecuting ? `Executing...` : `Execute ${selectedItems.length} Selected`}
              </Button>
            )}
          </Box>
          
          <ToggleButtonGroup
            value={filterStatus}
            exclusive
            onChange={(event, newValue) => {
              if (newValue !== null) {
                setFilterStatus(newValue);
              }
            }}
            size="small"
          >
            <ToggleButton value="pending" aria-label="pending">
              <PendingIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Pending
            </ToggleButton>
            <ToggleButton value="ready" aria-label="ready">
              <PlaylistAddCheckIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Ready
            </ToggleButton>
            <ToggleButton value="created" aria-label="created">
              <CheckCircleIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Created
            </ToggleButton>
            <ToggleButton value="all" aria-label="all">
              <AllInclusiveIcon sx={{ mr: 0.5, fontSize: 18 }} />
              All
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}


      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Data Table */}
      {!loading && !error && (
        <>
          <Paper elevation={2} sx={{ p: 0, borderRadius: 1, overflow: 'hidden' }}>
            <TableContainer>
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                  <TableCell padding="checkbox" sx={{ width: '5%' }}>
                    <Checkbox
                      indeterminate={isSomeSelected()}
                      checked={isAllSelected()}
                      onChange={handleSelectAll}
                      disabled={getDisplayData().filter(item => !item.executed && !item.page_id).length === 0}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '26%', py: 1, fontSize: '0.875rem' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '26%', py: 1, fontSize: '0.875rem' }}>Keywords</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '14%', py: 1, fontSize: '0.875rem' }}>Categories</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '10%', py: 1, fontSize: '0.875rem', textAlign: 'center' }}>Images</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '7%', py: 1, fontSize: '0.875rem', textAlign: 'center' }}>Exec</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '12%', py: 1, fontSize: '0.875rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getDisplayData().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="h6" color="text.secondary">
                        No inspiration found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try adjusting your search or filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getDisplayData().map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      selected={selectedItems.includes(item.id)}
                      sx={{
                        backgroundColor: item.id === hidingItemId ? '#e8f5e9' : 'inherit',
                        opacity: item.id === hidingItemId ? 0 : 1,
                        transform: item.id === hidingItemId ? 'scale(0.95)' : 'scale(1)',
                        transition: 'all 0.5s ease',
                        ...(item.content_created && generatingContent[item.id] === false ? {
                          animation: 'pulse 1.5s ease-in-out'
                        } : {})
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                          disabled={item.executed || item.page_id}
                        />
                      </TableCell>
                      <TableCell sx={{ wordWrap: 'break-word', overflow: 'hidden' }}>
                        {item.page_id ? (
                          // If content created, show title as read-only
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'medium',
                              opacity: 0.7,
                              cursor: 'not-allowed',
                              wordWrap: 'break-word'
                            }}
                          >
                            {item.title}
                          </Typography>
                        ) : editingTitleId === item.id ? (
                          // Inline editing mode for title
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 300 }}>
                            <TextField
                              size="small"
                              value={editedTitle}
                              onChange={(e) => setEditedTitle(e.target.value)}
                              placeholder="Enter title..."
                              sx={{ 
                                minWidth: 250,
                                '& .MuiInputBase-input': {
                                  fontSize: '0.875rem'
                                }
                              }}
                              multiline
                              rows={2}
                            />
                            <IconButton
                              size="small"
                              onClick={handleSaveTitle}
                              disabled={!editedTitle.trim() || savingTitle}
                              color="primary"
                            >
                              {savingTitle ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelTitleEditing}
                              disabled={savingTitle}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          // Display mode for title
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'medium',
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: '#f5f5f5' },
                              padding: '4px 8px',
                              borderRadius: '4px'
                            }}
                            onClick={() => handleStartTitleEditing(item)}
                          >
                            {item.title}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.page_id ? (
                          // If content created, show keywords as read-only
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              opacity: 0.7,
                              cursor: 'not-allowed'
                            }}
                          >
                            {truncateText(item.keywords, 150)}
                          </Typography>
                        ) : editingKeywordsId === item.id ? (
                          // Inline editing mode for keywords
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 300 }}>
                            <TextField
                              size="small"
                              value={editedKeywords}
                              onChange={(e) => setEditedKeywords(e.target.value)}
                              placeholder="Enter keywords..."
                              sx={{ 
                                minWidth: 350,
                                '& .MuiInputBase-input': {
                                  fontSize: '0.875rem'
                                }
                              }}
                              multiline
                              rows={3}
                            />
                            <IconButton
                              size="small"
                              onClick={handleSaveKeywords}
                              disabled={!editedKeywords.trim() || savingKeywords}
                              color="primary"
                            >
                              {savingKeywords ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelKeywordsEditing}
                              disabled={savingKeywords}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          // Display mode for keywords
                          <Tooltip title={item.keywords}>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#f5f5f5' },
                                padding: '4px 8px',
                                borderRadius: '4px'
                              }}
                              onClick={() => handleStartKeywordsEditing(item)}
                            >
                              {truncateText(item.keywords, 150)}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ wordWrap: 'break-word', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {/* Main Category */}
                          {item.category ? (
                            <Tooltip title={`Category: ${item.category} (ID: ${item.category_id})`}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'primary.main',
                                  fontWeight: 'medium',
                                  fontSize: '0.75rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                📁 {item.category}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ fontSize: '0.7rem' }}
                            >
                              📁 Not categorized
                            </Typography>
                          )}

                          {/* Special Ad Categories */}
                          {item.special_ad_categories && item.special_ad_categories.length > 0 ? (
                            <Tooltip title={`Special Ad Categories: ${item.special_ad_categories.join(', ')}`}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'secondary.main',
                                  fontSize: '0.75rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                🎯 {item.special_ad_categories.map(cat =>
                                  cat === 'NONE' ? 'None' : cat.replace(/_/g, ' ')
                                ).join(', ')}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ fontSize: '0.7rem' }}
                            >
                              🎯 Not specified
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {item.has_images ? (
                          // Has images generated
                          <Tooltip title="Images generated">
                            <Chip
                              icon={<ImageIcon />}
                              label="✓"
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          // No images yet
                          <Tooltip title="No images generated yet">
                            <Chip
                              icon={<ImageNotSupportedIcon />}
                              label="✗"
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {item.executed ? (
                          <Tooltip title="Job has been executed">
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Yes"
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Job not executed yet">
                            <Chip
                              icon={<PendingIcon />}
                              label="No"
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title={
                            generatingContent[item.id] ? "Generating content..." :
                            item.page_id ? "Content created - Click to re-generate" :
                            item.executed ? "Job executed - awaiting content creation" :
                            "Generate Content Page"
                          }>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleGenerateContentClick(item)}
                                disabled={generatingContent[item.id]}
                                color={
                                  generatingContent[item.id] ? "primary" :
                                  item.page_id ? "success" :
                                  item.executed ? "warning" :
                                  "primary"
                                }
                              >
                                {generatingContent[item.id] ? (
                                  <CircularProgress size={16} />
                                ) : item.page_id ? (
                                  <RefreshIcon fontSize="small" />
                                ) : item.executed ? (
                                  <PendingIcon fontSize="small" />
                                ) : (
                                  <AutoFixHighIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete Item">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(item)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Paper>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Category Selection Dialog */}
      <Dialog open={categoryDialogOpen} onClose={handleCategoryDialogCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Select Category for Content Generation
        </DialogTitle>
        <DialogContent>
          {selectedItemForGeneration && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {selectedItemForGeneration.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Keywords: {selectedItemForGeneration.keywords}
              </Typography>
            </Box>
          )}
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please select a category for this content:
          </Typography>
          
          <Autocomplete
            freeSolo
            options={dialogCategoryOptions}
            value={dialogSelectedCategory}
            onChange={(event, value) => {
              if (typeof value === 'string') {
                setDialogSelectedCategory({ id: null, name: value });
              } else {
                setDialogSelectedCategory(value);
              }
            }}
            onInputChange={(event, value) => {
              // Debounced search
              if (window.dialogCategorySearchTimeout) {
                clearTimeout(window.dialogCategorySearchTimeout);
              }
              
              window.dialogCategorySearchTimeout = setTimeout(() => {
                searchDialogCategories(value || '');
              }, 300);

              if (value && !dialogCategoryOptions.find(cat => cat.name === value)) {
                setDialogSelectedCategory({ id: null, name: value });
              }
            }}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option?.name || '';
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                placeholder="Search for a category..."
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {dialogLoadingCategories ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            loading={dialogLoadingCategories}
          />

          <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
            Special Ad Categories (Facebook):
          </Typography>

          <Autocomplete
            multiple
            options={specialAdCategories}
            value={specialAdCategories.filter(cat => dialogSelectedSpecialAdCategories.includes(cat.value))}
            onChange={(event, newValue) => {
              const newValues = newValue.map(item => item.value);

              // Handle "NONE" selection logic
              if (newValues.includes('NONE') && newValues.length > 1) {
                // If NONE is selected along with others, keep only the non-NONE values
                const nonNoneValues = newValues.filter(val => val !== 'NONE');
                setDialogSelectedSpecialAdCategories(nonNoneValues.length > 0 ? nonNoneValues : ['NONE']);
              } else if (newValues.length === 0) {
                // If nothing is selected, default to NONE
                setDialogSelectedSpecialAdCategories(['NONE']);
              } else {
                setDialogSelectedSpecialAdCategories(newValues);
              }
            }}
            getOptionLabel={(option) => option.label}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {option.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </Box>
            )}
            renderTags={(tagValue, getTagProps) => (
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label}
                  size="small"
                  color={option.value === 'NONE' ? 'default' : 'primary'}
                />
              ))
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Special Ad Categories"
                placeholder="Select categories that apply to this content..."
                fullWidth
              />
            )}
            size="small"
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              Special ad categories are required by Facebook for certain types of content.
              Select "None" if your content doesn't fall into any of these categories.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCategoryDialogCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleCategoryDialogConfirm}
            variant="contained"
            disabled={!dialogSelectedCategory?.name?.trim()}
            startIcon={<AutoFixHighIcon />}
          >
            Generate Content
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this inspiration item?
          </Typography>
          {itemToDelete && (
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {itemToDelete.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Category: {itemToDelete.category || 'No category'}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteItem}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={manualDialogOpen} onClose={handleCloseManualDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Manual Inspiration Entry
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Title"
              fullWidth
              required
              value={manualTitle}
              onChange={(e) => setManualTitle(e.target.value)}
              placeholder="Enter campaign title or idea"
              helperText="A descriptive title for your inspiration"
            />

            <TextField
              label="Keywords"
              fullWidth
              required
              multiline
              rows={3}
              value={manualKeywords}
              onChange={(e) => setManualKeywords(e.target.value)}
              placeholder="keyword1, keyword2, keyword3"
              helperText="Comma-separated keywords for targeting"
            />

            <Autocomplete
              multiple
              options={specialAdCategories}
              getOptionLabel={(option) => option.label}
              value={specialAdCategories.filter(cat => manualSpecialCategories.includes(cat.value))}
              onChange={(event, newValue) => {
                setManualSpecialCategories(newValue.map(v => v.value));
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Special Ad Categories (Optional)"
                  placeholder="Select categories"
                  helperText="Facebook special ad categories if applicable"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManualDialog} disabled={savingManual}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveManualEntry}
            variant="contained"
            disabled={savingManual || !manualTitle.trim() || !manualKeywords.trim()}
            startIcon={savingManual ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {savingManual ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Upload CSV File
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Upload a CSV file with titles and keywords to add to the inspiration database.
            ChatGPT will validate and optimize the content before adding.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Button
              variant="text"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              size="small"
            >
              Download CSV Template
            </Button>
          </Box>

          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              border: '2px dashed #ccc', 
              borderRadius: 2,
              bgcolor: '#fafafa',
              textAlign: 'center',
              mb: 2
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            
            {uploadFile ? (
              <Box>
                <FileUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">{uploadFile.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {(uploadFile.size / 1024).toFixed(2)} KB
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setUploadFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  sx={{ mt: 2 }}
                  size="small"
                >
                  Remove File
                </Button>
              </Box>
            ) : (
              <Box>
                <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Select CSV File
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose File
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                  Format: Title, Keywords (comma-separated)
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Blacklist Words Input */}
          <TextField
            fullWidth
            label="Blacklist Words (optional)"
            placeholder="e.g., job, career, education, scholarship, clinical trial"
            value={blacklistWords}
            onChange={(e) => setBlacklistWords(e.target.value)}
            helperText="Comma-separated words. GPT will reject any items related to these topics."
            sx={{ mb: 2 }}
            disabled={uploading}
          />

          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Processing CSV file...
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {csvJobId && csvJobStatus && csvJobStatus !== 'completed' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {csvProcessingMessage || 'Processing in background...'}
              </Typography>
              <LinearProgress
                variant={csvProgress > 0 ? "determinate" : "indeterminate"}
                value={csvProgress}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Status: {csvJobStatus}
                </Typography>
                {csvProgress > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(csvProgress)}%
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {uploadResults && (
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              {uploadResults.success ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    CSV processed successfully!
                  </Alert>

                  {/* Detailed Flow Summary */}
                  {uploadResults.detailedSummary && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Processing Flow:</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        <Chip label={`${uploadResults.detailedSummary.original_rows || 0} loaded`} size="small" />
                        <Typography variant="body2">→</Typography>
                        <Chip label={`${uploadResults.detailedSummary.pre_filter_skipped || 0} pre-filtered`} size="small" color="warning" variant="outlined" />
                        <Typography variant="body2">→</Typography>
                        <Chip label={`${uploadResults.detailedSummary.sent_to_gpt || 0} to GPT`} size="small" color="info" variant="outlined" />
                        <Typography variant="body2">→</Typography>
                        <Chip label={`${uploadResults.detailedSummary.gpt_rejected || 0} GPT rejected`} size="small" color="error" variant="outlined" />
                        <Typography variant="body2">→</Typography>
                        <Chip label={`${uploadResults.detailedSummary.post_filter_skipped || 0} post-filtered`} size="small" color="warning" variant="outlined" />
                        <Typography variant="body2">→</Typography>
                        <Chip label={`${uploadResults.detailedSummary.added || 0} added`} size="small" color="success" />
                      </Box>
                    </Box>
                  )}

                  {uploadResults.added && uploadResults.added.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        ✅ Added ({uploadResults.added.length}):
                      </Typography>
                      <List dense>
                        {uploadResults.added.slice(0, 5).map((item, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={item.title}
                              secondary={item.keywords}
                            />
                          </ListItem>
                        ))}
                        {uploadResults.added.length > 5 && (
                          <ListItem>
                            <ListItemText
                              primary={`... and ${uploadResults.added.length - 5} more`}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  )}

                  {/* Skipped by Category */}
                  {uploadResults.skippedByCategory && (
                    <Box sx={{ mb: 2 }}>
                      {uploadResults.skippedByCategory.pre_filter?.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            ⚠️ Pre-filter Skipped ({uploadResults.skippedByCategory.pre_filter.length}):
                          </Typography>
                          <List dense sx={{ pl: 2 }}>
                            {uploadResults.skippedByCategory.pre_filter.slice(0, 3).map((item, index) => (
                              <ListItem key={index} sx={{ py: 0 }}>
                                <ListItemText
                                  primary={<Typography variant="body2">{item.title}</Typography>}
                                  secondary={<Typography variant="caption" color="warning.main">{item.reason}</Typography>}
                                />
                              </ListItem>
                            ))}
                            {uploadResults.skippedByCategory.pre_filter.length > 3 && (
                              <ListItem sx={{ py: 0 }}>
                                <ListItemText primary={<Typography variant="body2" color="text.secondary">... and {uploadResults.skippedByCategory.pre_filter.length - 3} more</Typography>} />
                              </ListItem>
                            )}
                          </List>
                        </Box>
                      )}

                      {uploadResults.skippedByCategory.gpt_rejected?.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            🤖 GPT Rejected ({uploadResults.skippedByCategory.gpt_rejected.length}):
                          </Typography>
                          <List dense sx={{ pl: 2 }}>
                            {uploadResults.skippedByCategory.gpt_rejected.slice(0, 5).map((item, index) => (
                              <ListItem key={index} sx={{ py: 0 }}>
                                <ListItemText
                                  primary={<Typography variant="body2">{item.title}</Typography>}
                                  secondary={<Typography variant="caption" color="error.main">{item.reason}</Typography>}
                                />
                              </ListItem>
                            ))}
                            {uploadResults.skippedByCategory.gpt_rejected.length > 5 && (
                              <ListItem sx={{ py: 0 }}>
                                <ListItemText primary={<Typography variant="body2" color="text.secondary">... and {uploadResults.skippedByCategory.gpt_rejected.length - 5} more</Typography>} />
                              </ListItem>
                            )}
                          </List>
                        </Box>
                      )}

                      {uploadResults.skippedByCategory.post_filter?.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            🔍 Post-filter Skipped ({uploadResults.skippedByCategory.post_filter.length}):
                          </Typography>
                          <List dense sx={{ pl: 2 }}>
                            {uploadResults.skippedByCategory.post_filter.slice(0, 3).map((item, index) => (
                              <ListItem key={index} sx={{ py: 0 }}>
                                <ListItemText
                                  primary={<Typography variant="body2">{item.title}</Typography>}
                                  secondary={<Typography variant="caption" color="info.main">{item.reason}</Typography>}
                                />
                              </ListItem>
                            ))}
                            {uploadResults.skippedByCategory.post_filter.length > 3 && (
                              <ListItem sx={{ py: 0 }}>
                                <ListItemText primary={<Typography variant="body2" color="text.secondary">... and {uploadResults.skippedByCategory.post_filter.length - 3} more</Typography>} />
                              </ListItem>
                            )}
                          </List>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                <Alert severity="error">
                  {uploadResults.error || 'Failed to process CSV file'}
                </Alert>
              )}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>
            Close
          </Button>
          <Button 
            onClick={handleUploadCSV}
            variant="contained"
            disabled={!uploadFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <FileUploadIcon />}
          >
            {uploading ? 'Processing...' : 'Upload & Process'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Confirmation Dialog */}
      <Dialog
        open={confirmationDialogOpen}
        onClose={handleCancelConfirmation}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Confirm Items to Add ({selectedPendingItems.length} of {pendingItems.length} selected)
            </Typography>
            <Box>
              <Button
                size="small"
                onClick={handleSelectAllPending}
                sx={{ mr: 1 }}
              >
                {selectedPendingItems.length === pendingItems.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Review the items below and uncheck any you don't want to add. Only selected items will be inserted into the database.
          </Alert>

          {pendingItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No items to confirm
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell padding="checkbox" sx={{ width: '5%' }}>
                      <Checkbox
                        checked={selectedPendingItems.length === pendingItems.length}
                        indeterminate={selectedPendingItems.length > 0 && selectedPendingItems.length < pendingItems.length}
                        onChange={handleSelectAllPending}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Keywords</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Category</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingItems.map((item, index) => (
                    <TableRow
                      key={item.temp_id}
                      hover
                      selected={selectedPendingItems.includes(item.temp_id)}
                      onClick={() => handleTogglePendingItem(item.temp_id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedPendingItems.includes(item.temp_id)}
                          onChange={() => handleTogglePendingItem(item.temp_id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {item.keywords}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.special_ad_categories?.[0] === 'NONE' ? 'None' : item.special_ad_categories?.[0]?.replace(/_/g, ' ') || 'None'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelConfirmation} disabled={confirmingItems}>
            Cancel (Don't Add Any)
          </Button>
          <Button
            onClick={handleConfirmItems}
            variant="contained"
            color="primary"
            disabled={selectedPendingItems.length === 0 || confirmingItems}
            startIcon={confirmingItems ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          >
            {confirmingItems ? 'Adding...' : `Add ${selectedPendingItems.length} Selected Items`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="info" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      </Container>
    </>
  );
};

export default RsocInspiration;